import Link from "next/link";
import Image from "next/image";
import { ReactElement, useCallback, useEffect, useState } from "react";
import AuthLayout from "../components/layout/auth";
import { useAuth } from "../hooks/auth";
import validator from "validator";
import ErrorCallout from "../components/callouts/error";
import Spinner from "../components/spinner";
import { GetServerSideProps } from "next/types";
import Unprotected from "../server/unprotected";
import { useRouter } from "next/router";
import PrimaryButton from "../components/buttons/primary";

export default function SignIn({}: {}) {
  const router = useRouter();
  const { signIn, loginWithAmazon, loginWithAmazonError, configured } =
    useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signUpFieldsRequired, setSignUpFieldsRequired] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(
    !!router.query.code || !!router.query.email
  );

  useEffect(() => {
    async function signInWithTempCreds(
      tempEmail: string,
      tempPassword: string
    ) {
      try {
        await signIn(tempEmail, tempPassword);
      } catch (error) {
        console.log(error);
      }
    }
    if (!!router.query.email && configured) {
      signInWithTempCreds(
        router.query.email as string,
        router.query.password as string
      );
    }
  }, [router, configured, signIn]);

  // Login with Amazon redirectee's should see loading screen while waiting
  useEffect(() => {
    if (!!router.query.code || !!router.query.email) {
      const timer = setTimeout(() => {
        setLoading(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [router, setLoading]);

  const handleSignIn = useCallback(async () => {
    if (!validator.isEmail(email) || password.length < 7) {
      setSignUpFieldsRequired(true);
      return;
    }
    try {
      await signIn(email, password);
    } catch (error) {
      if (error instanceof Error) {
        setSignInError(error.message);
      }
    }
  }, [email, password, setSignUpFieldsRequired, signIn]);

  if (loading) {
    return (
      <>
        <div className="flex">
          <Spinner className="fill-primary-base h-14 w-14 m-auto"></Spinner>
        </div>
      </>
    );
  } else {
    return (
      <>
        {signInError ? (
          <div className="mb-10">
            <ErrorCallout subTitle={signInError}></ErrorCallout>
          </div>
        ) : null}
        {loginWithAmazonError ? (
          <div className="mb-10">
            <ErrorCallout subTitle={loginWithAmazonError}></ErrorCallout>
          </div>
        ) : null}

        <div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <button
                onClick={async () => await loginWithAmazon(true)}
                className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <Image
                  src="/amazon-icon.svg"
                  alt="Login with Amazon"
                  width="16px"
                  height="16px"
                />
                <span className="ml-2.5 text-sm">Login with Amazon</span>
              </button>
            </div>
          </div>

          <div className="relative mt-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Or continue with
              </span>
            </div>
          </div>
        </div>

        <form className="space-y-6 mt-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email address
            </label>
            <div className="mt-1">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-vendrive-500 focus:border-vendrive-500 sm:text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required={signUpFieldsRequired}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700"
            >
              Password
            </label>
            <div className="mt-1">
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-vendrive-500 focus:border-vendrive-500 sm:text-sm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required={signUpFieldsRequired}
              />
            </div>
          </div>

          <div className="flex items-center justify-end">
            <div className="text-sm">
              <Link href="/forgotpassword">
                <a className="font-medium text-vendrive-600 hover:text-vendrive-500">
                  Forgot your password?
                </a>
              </Link>
            </div>
          </div>

          <div>
            <PrimaryButton className="w-full" onClick={handleSignIn}>
              Sign in
            </PrimaryButton>
          </div>
        </form>
      </>
    );
  }
}

SignIn.getLayout = function getLayout(page: ReactElement) {
  return (
    <AuthLayout
      title="Sign in"
      heading="Sign in to your account"
      altOptionText="sign up to review prep centers"
      altOptionLink="/signup"
    >
      {page}
    </AuthLayout>
  );
};

export const getServerSideProps: GetServerSideProps = Unprotected();
